import { isEqual } from 'lodash';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import produce from 'immer';

import {
	CollaboratorDetails,
	CollaboratorPermissions,
	Views as ViewsComponent
} from 'components/Collaborators';
import { DefaultCollaboratorPermissions } from 'consts';
import { initButtonProps } from 'helpers/buttons';
import { buildInitialPermissions } from 'helpers/collaborators';
import {
	Collaborator,
	GeneralPermissionFlags,
	GeneralPermissions as GeneralPermissionsType,
	ModulesPermissions as ModulesPermissionsType,
	StatusTypeAccesses
} from 'store/data/collaborators';
import { Modal } from 'components/UI/Modal';
import { Flex } from 'components/UI/Flex';
import { Button } from 'components/UI/Interactables/Button';
import { CreatableSelect } from 'components/UI/Interactables/CreatableSelect';
import { Spacer } from 'components/UI/Spacer';
import {
	useTranslation,
	useIsProjectOwner,
	useUpdateCollaboratorPermissions,
	useStatuses,
	useRoles,
	useOrganizationsByCollaborator,
	useProjectOrganizations,
	useCollaboratorsData,
	useAddCollaboratorsToOrganization,
	usePermissions
} from 'hooks/store';
import { useKeyPress, useCompletedAction, useSelector } from 'hooks/utils';
import { GroupContainer } from './CollaboratorModal.style';
import { Typography } from 'components/UI/Typography';
import { SelectItem } from 'types/index';
import { useCurrentUserQuery } from 'features/data/amplify/useCurrentUserQuery';
import { selectProjectOwner } from '../../../../store/data/projects';
import { useAlerts } from '../../../../hooks/ui';

enum ViewTabs {
	Information = 'information',
	Permissions = 'permissions',
	Views = 'views',
	Groups = 'groups'
}

interface Props {
	writeAccess: boolean;
	collaborator: Collaborator;
	removeModalVisible: boolean;
	onClose: (success?: boolean) => void;
	onRemove: (collaboratorId: string) => void;
	onRemoveCollaborator: (input: { organizationId: string; collaboratorId: string }) => void;
	projectId?: string;
}

export function CollaboratorModal({
	writeAccess,
	collaborator,
	removeModalVisible,
	onClose,
	onRemove,
	onRemoveCollaborator,
	projectId
}: Props) {
	const { translate } = useTranslation();

	const currentUserQuery = useCurrentUserQuery();

	const collaboratorOrganizations = useOrganizationsByCollaborator(collaborator.userId);
	const { organizationsMap } = useCollaboratorsData();
	const [
		{ loading: updatingCollaboratorPermissions, error: errorUpdatingCollaboratorPermissions },
		updateCollaboratorPermissions
	] = useUpdateCollaboratorPermissions();
	const [
		{ loading: addingCollaboratorsToGroups, error: errorAddingCollaboratorsToGroups },
		addCollaboratorsToOrganizations
	] = useAddCollaboratorsToOrganization();
	const organizations = useProjectOrganizations();
	const [organizationsToAdd, setOrganizationsToAdd] = useState<string[]>([]);

	const isProjectOwner = useIsProjectOwner();
	const isSelf = collaborator.userId === currentUserQuery.data?.username;

	const projectOwner = useSelector(state => selectProjectOwner(state.data.projects, projectId));

	const collaboratorIsProjectOwner = collaborator.userId === projectOwner;

	const { setError } = useAlerts();

	const [
		{
			data: { statuses }
		}
	] = useStatuses({ lazy: true, omitSystemGenerated: false });

	const [
		{
			data: { roles }
		}
	] = useRoles();

	const roleSelectItems = useMemo(() => {
		const selectItems = [];

		for (const role of roles) {
			selectItems.push({
				label: role.name,
				value: role.id
			});
		}

		return selectItems;
	}, [roles]);

	const organizationSelectItems = useMemo(() => {
		const orgs: SelectItem[] = [];

		organizations.forEach(org => {
			if (
				!org.collaborators.includes(collaborator.userId ?? '') &&
				!organizationsToAdd.includes(org.id)
			)
				orgs.push({
					label: org.name,
					value: org.id
				});
		});

		return orgs;
	}, [organizations, organizationsToAdd, collaborator.userId]);

	const selectedItems: SelectItem[] = useMemo(() => {
		return organizationsToAdd.map(orgId => ({
			label: organizationsMap[orgId].name,
			value: orgId
		}));
	}, [organizationsToAdd, organizationsMap]);

	const collaboratorRoleId = collaborator.projectRoleId || null;

	const [assignedRoleId, setAssignedRoleId] = useState<string | null>(collaboratorRoleId);

	const initialPermissions = useMemo(() => {
		const initial = collaborator.accessToProject ?? DefaultCollaboratorPermissions;

		const permissions = buildInitialPermissions(initial, statuses);

		return permissions;
	}, [collaborator, statuses]);

	const [permissions, setPermissions] = useState(initialPermissions);

	const havePermissionsChanged = useMemo(
		() => !isEqual(permissions, initialPermissions),
		[permissions, initialPermissions]
	);

	const isSameRole = assignedRoleId === collaboratorRoleId;

	const { canAddCollaborator } = usePermissions(projectId ? { projectId } : undefined);
	const { canRemoveCollaborator } = usePermissions(projectId ? { projectId } : undefined);
	const disabled = !canAddCollaborator || !!assignedRoleId;

	const onChangePermissions = {
		general(generalPermissions: GeneralPermissionsType) {
			const updatedPermissions = produce(permissions, draft => {
				for (const key in generalPermissions) {
					const value = generalPermissions[key as GeneralPermissionFlags];
					draft[key as GeneralPermissionFlags] = value;
				}
			});
			setPermissions(updatedPermissions);
		},
		statuses(statusesPermissions: StatusTypeAccesses) {
			const updatedPermissions = produce(permissions, draft => {
				draft.statusTypeAccesses = statusesPermissions;
			});
			setPermissions(updatedPermissions);
		},
		modules(modulesPermissions: ModulesPermissionsType) {
			const updatedPermissions = produce(permissions, draft => {
				draft.modules = modulesPermissions;
			});
			setPermissions(updatedPermissions);
		}
	};

	const views = {
		order: [ViewTabs.Information, ViewTabs.Permissions, ViewTabs.Views, ViewTabs.Groups],
		labels: {
			[ViewTabs.Information]: translate(
				dict => dict.collaborators.collaboratorModal.information
			),
			[ViewTabs.Permissions]: translate(
				dict => dict.collaborators.collaboratorModal.permissions
			),
			[ViewTabs.Views]: translate(dict => dict.collaborators.collaboratorModal.views),
			[ViewTabs.Groups]: translate(dict => dict.collaborators.collaboratorModal.groups)
		}
	};

	const [view, setView] = useState<ViewTabs>(ViewTabs.Information);

	useKeyPress(
		{ onDeleteKeyPress: () => onRemove(collaborator.userId) },
		{ listen: canDelete() && !isLoadingAction() && !removeModalVisible }
	);

	useKeyPress(
		{ onEnterKeyPress: havePermissionsChanged ? handleSubmit : onClose },
		{ listen: !isLoadingAction() && !removeModalVisible }
	);

	useCompletedAction(updatingCollaboratorPermissions, errorUpdatingCollaboratorPermissions, () =>
		onClose(true)
	);

	useCompletedAction(addingCollaboratorsToGroups, errorAddingCollaboratorsToGroups, () =>
		onClose(true)
	);

	function handleSelectItemsChange(value: string | null | undefined) {
		if (!value) {
			setPermissions(initialPermissions);
			setAssignedRoleId(null);
		} else {
			if (collaboratorRoleId && value === collaboratorRoleId) {
				setPermissions(initialPermissions);
				setAssignedRoleId(value);
				return;
			}

			const role = roles.find(role => role.id === value);

			if (role) {
				const permissions = buildInitialPermissions(role.permissions, statuses);

				setAssignedRoleId(role.id);
				setPermissions(permissions);
			}
		}
	}

	function handleSubmit() {
		if (isLoadingAction()) return;

		updateCollaboratorPermissions({
			collaboratorId: collaborator.userId,
			permissions,
			projectRoleId: assignedRoleId
		});

		if (selectedItems.length) {
			addCollaboratorsToOrganizations({
				collaborators: [collaborator.userId],
				organizationIds: organizationsToAdd
			});
		}
	}

	function canDelete(): boolean {
		return (
			((canRemoveCollaborator ?? false) && !isSelf) || ((isProjectOwner ?? false) && !isSelf)
		);
	}

	function isLoadingAction(): boolean {
		return updatingCollaboratorPermissions || addingCollaboratorsToGroups;
	}

	function isValid() {
		if (collaboratorRoleId)
			return !havePermissionsChanged && isSameRole && selectedItems.length === 0;

		return !havePermissionsChanged && selectedItems.length === 0;
	}

	const buttonProps = initButtonProps(buttons => {
		buttons.primary = {
			label: translate(({ buttons }) => buttons.update),
			loading: isLoadingAction(),
			onClick: handleSubmit
		};

		buttons.neutral = {
			label: translate(({ buttons }) => buttons.cancel),
			onClick: onClose
		};

		if (isValid()) {
			delete buttons.neutral;

			buttons.primary = {
				label: translate(({ buttons }) => buttons.done),
				onClick: onClose
			};
		}
	});

	return (
		<Modal
			size={s => s.m}
			title={translate(dict => dict.collaborators.collaboratorModal.collaboratorDetails)}
			primary={buttonProps.primary}
			neutral={buttonProps.neutral}
			tabs={{
				labels: views.order.map(view => views.labels[view]),
				active: views.order.indexOf(view),
				onClick: tabIndex => setView(views.order[tabIndex])
			}}
			onClose={onClose}
			fullSizeConfig={{
				narrow: true
			}}
			visible
			close
		>
			{/* INFORMATION VIEW */}
			{view === ViewTabs.Information && (
				<>
					<CollaboratorDetails
						isProjectOwner={!!(isProjectOwner && isSelf)}
						isSelf={isSelf}
						collaborator={collaborator}
					/>

					{canDelete() && (
						<Flex flex={1} align={a => a.end} marginOffset={{ top: 2.4 }}>
							<Button
								variant={v => v.link}
								paddingOffset={{ all: 0 }}
								title={translate(
									({ collaborators }) => collaborators.details.remove.project
								)}
								onClick={() =>
									collaboratorIsProjectOwner
										? setError({
												message: translate(
													({ admin }) => admin.users.errors.removedOwner
												)
										  })
										: onRemove(collaborator.userId)
								}
							/>
						</Flex>
					)}
				</>
			)}

			{/* PERMISSIONS VIEW */}
			{view === ViewTabs.Permissions && (
				<>
					{!isSelf && (
						<>
							<InputWrapper>
								<CreatableSelect
									label={translate(
										dict => dict.collaborators.collaboratorModal.role
									)}
									placeholder={translate(
										dict => dict.collaborators.collaboratorModal.selectRole
									)}
									items={roleSelectItems}
									value={roleSelectItems.find(
										item => item.value === assignedRoleId
									)}
									onValueSelected={handleSelectItemsChange}
								/>
							</InputWrapper>

							<Spacer size={s => s.m} />
						</>
					)}

					<CollaboratorPermissions
						metadata={{
							isSelf,
							isProjectOwner: !!isProjectOwner,
							projectId: projectId
						}}
						statuses={statuses}
						permissions={permissions}
						onChange={setPermissions}
						disabled={!!assignedRoleId}
					/>
				</>
			)}

			{/* VIEWS VIEW */}
			{view === ViewTabs.Views && (
				<ViewsComponent
					permissions={permissions.modules}
					disabled={disabled}
					isProjectOwner={!!isProjectOwner}
					isSelf={isSelf}
					onChange={onChangePermissions.modules}
				/>
			)}

			{/* GROUPS VIEW */}
			{view === ViewTabs.Groups && (
				<Flex column>
					<CreatableSelect
						items={organizationSelectItems}
						onValuesSelected={values => setOrganizationsToAdd(values)}
						values={selectedItems}
						label={translate(dict => dict.collaborators.organizationModal.addGroup)}
						hasMultipleValues
					/>
					<Spacer size={s => s.l} />
					{collaboratorOrganizations.length > 0 && (
						<Typography.Paragraph marginOffset={{ bottom: 0.8 }}>
							{translate(dict => dict.collaborators.organizationModal.groupsList)}
						</Typography.Paragraph>
					)}
					{collaboratorOrganizations.map(org => (
						<GroupContainer key={org.id}>
							<Typography.Paragraph>{org.name}</Typography.Paragraph>
							{writeAccess && (
								<Button
									className="remove-button"
									title={translate(dict => dict.buttons.remove)}
									variant={v => v.link}
									paddingOffset={{ all: 0 }}
									onClick={() =>
										onRemoveCollaborator({
											collaboratorId: collaborator.userId,
											organizationId: org.id
										})
									}
								/>
							)}
						</GroupContainer>
					))}
				</Flex>
			)}
		</Modal>
	);
}

const InputWrapper = styled.div`
	width: 50%;
`;
